import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
    children: ReactNode;
}

interface State {
    error: Error | null;
    info: ErrorInfo | null;
}

// Catches render-time crashes anywhere in the app tree and shows the real
// error instead of leaving the page blank (yellow CSS background, nothing
// else) — the boot-time script in index.html covers import/parse-time
// crashes that happen before React even runs; this covers everything after.
export default class RootErrorBoundary extends Component<Props, State> {
    state: State = { error: null, info: null };

    static getDerivedStateFromError(error: Error) {
        return { error, info: null };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        this.setState({ error, info });
    }

    render() {
        if (this.state.error) {
            return (
                <pre style={{
                    position: 'fixed', inset: 0, background: '#fff', color: '#c00',
                    padding: 16, font: '12px/1.4 monospace', whiteSpace: 'pre-wrap',
                    zIndex: 2147483647, overflow: 'auto', margin: 0,
                }}>
                    {'React render error: ' + (this.state.error.stack || this.state.error.message)}
                    {this.state.info ? '\n\nComponent stack:' + this.state.info.componentStack : ''}
                </pre>
            );
        }
        return this.props.children;
    }
}
